import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { filesApi } from '../../api/files';
import { foldersApi } from '../../api/folders';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toaster';

export function RenameModal() {
  const { t } = useTranslation('files');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = useUIStore((s) => s.isRenameModalOpen);
  const closeModal = useUIStore((s) => s.closeRenameModal);
  const contextMenuTarget = useUIStore((s) => s.contextMenuTarget);

  const files = useFileStore((s) => s.files);
  const folders = useFileStore((s) => s.folders);
  const updateFile = useFileStore((s) => s.updateFile);
  const updateFolder = useFileStore((s) => s.updateFolder);
  const toast = useToast();

  // Get the current item being renamed
  const currentItem =
    contextMenuTarget?.type === 'file'
      ? files.find((f) => f.id === contextMenuTarget.id)
      : folders.find((f) => f.id === contextMenuTarget?.id);

  useEffect(() => {
    if (isOpen && currentItem) {
      setName(currentItem.name);
    }
  }, [isOpen, currentItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !contextMenuTarget) return;

    setIsLoading(true);
    try {
      if (contextMenuTarget.type === 'file') {
        const { file } = await filesApi.update(contextMenuTarget.id, {
          name: name.trim(),
        });
        updateFile(contextMenuTarget.id, file);
        toast.success(t('toast.fileRenamed'), t('toast.renamedTo', { name: file.name }));
      } else {
        const { folder } = await foldersApi.update(contextMenuTarget.id, {
          name: name.trim(),
        });
        updateFolder(contextMenuTarget.id, folder);
        toast.success(t('toast.folderRenamed'), t('toast.renamedTo', { name: folder.name }));
      }
      handleClose();
    } catch {
      toast.error(t('toast.renameFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={contextMenuTarget?.type === 'folder' ? t('rename.folderTitle') : t('rename.fileTitle')}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('common:labels.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('rename.placeholder')}
          autoFocus
          required
        />

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1"
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!name.trim() || name === currentItem?.name}
            className="flex-1"
          >
            {t('common:buttons.rename')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
